import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { format, subDays } from 'date-fns';
import { clickPerformanceApi } from '../../services/api';
import { NetworkHeroKPIs } from './index';
import { formatHours } from '../../utils/formatters';
import ClickVolumeTrends from './ClickVolumeTrends';
import ClickPerformanceTrends from './ClickPerformanceTrends';
import RetailerClickTable from './RetailerClickTable';
import RetailerPartnerDrilldown from './RetailerPartnerDrilldown';
import PerformanceTabBar from './PerformanceTabBar';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

const PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

const CLICK_PERFORMANCE_CARDS = [
  {
    key: 'total_clicks',
    label: 'Clicks',
    color: '#1976d2',
    format: (v) => (v || 0).toLocaleString(),
    tooltip: 'Total clicks routed through the network in this period.',
  },
  {
    key: 'total_approved_orders',
    label: 'Orders',
    color: '#2e7d32',
    format: (v) => (v || 0).toLocaleString(),
    tooltip: 'Approved orders from clicks in this period (filtered by click date, not order date). Will differ slightly from the Orders tab, which filters by order date.',
  },
  {
    key: 'network_conversion_rate',
    label: 'Conv %',
    color: '#9c27b0',
    format: (v) => v != null ? `${(v * 100).toFixed(1)}%` : '—',
    tooltip: 'Clicks that resulted in an approved order. Includes orders within 30 days of click — 97% convert within 3 days.',
  },
  {
    key: 'avg_conversion_hours',
    label: 'Avg time',
    color: '#ed6c02',
    format: formatHours,
    tooltip: 'Average time from click to approved order (weighted by order count). Pulled up by a long tail of slow conversions.',
  },
  {
    key: 'p50_conversion_hours',
    label: 'p50 time',
    color: '#0288d1',
    format: formatHours,
    tooltip: 'Median conversion time — half of all orders converted faster than this.',
  },
  {
    key: 'p90_conversion_hours',
    label: 'p90 time',
    color: '#546e7a',
    format: formatHours,
    tooltip: '90th percentile conversion time — 90% of orders converted within this window.',
  },
];

export default function ClickPerformanceDashboard() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const thirtyDaysAgo = format(subDays(new Date(), 30), 'yyyy-MM-dd');

  const [fromDate, setFromDate] = useState(thirtyDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);
  const [drilldownId, setDrilldownId] = useState(null);
  const [drilldownName, setDrilldownName] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await clickPerformanceApi.getSummary({
        from_date: fromDate,
        to_date: toDate,
      });
      if (result.success) {
        setSummary(result.data);
      } else {
        setError(result.error || 'Failed to load click performance data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const applyPreset = (days) => {
    setFromDate(format(subDays(new Date(), days), 'yyyy-MM-dd'));
    setToDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const handleDrilldown = (retailerId, retailerName) => {
    setDrilldownId(retailerId);
    setDrilldownName(retailerName);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Click-to-order performance
      </Typography>
      <PerformanceTabBar />

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1.5, sm: 2 },
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            label="From"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="To"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            sx={{ flex: 1 }}
          />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {PRESETS.map((p) => (
            <Chip
              key={p.days}
              label={p.label}
              size="small"
              onClick={() => applyPreset(p.days)}
              variant={fromDate === format(subDays(new Date(), p.days), 'yyyy-MM-dd') ? 'filled' : 'outlined'}
              color={fromDate === format(subDays(new Date(), p.days), 'yyyy-MM-dd') ? 'primary' : 'default'}
            />
          ))}
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <NetworkHeroKPIs data={summary?.network} cards={CLICK_PERFORMANCE_CARDS} />
          <ClickVolumeTrends fromDate={fromDate} toDate={toDate} />
          <ClickPerformanceTrends fromDate={fromDate} toDate={toDate} />
          <RetailerClickTable retailers={summary?.retailers} onDrilldown={handleDrilldown} />
        </Stack>
      )}

      <RetailerPartnerDrilldown
        open={!!drilldownId}
        onClose={() => setDrilldownId(null)}
        retailerId={drilldownId}
        retailerName={drilldownName}
        fromDate={fromDate}
        toDate={toDate}
      />
    </Box>
  );
}
